/**
 * Evidence Basket Manager Component
 * Manages selected evidence items for proposal generation
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Badge } from '@tracekit/core';
import { X, FileText, Trash2, Download } from 'lucide-react';
import { EvidenceBasketItem } from '../types/rfp';

const EVIDENCE_BASKET_STORAGE_KEY = 'rfp-evidence-basket';

export function EvidenceBasket() {
  const [basket, setBasket] = useState<EvidenceBasketItem[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(EVIDENCE_BASKET_STORAGE_KEY);
    if (stored) {
      try {
        setBasket(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load evidence basket:', e);
      }
    }
  }, []);

  // Save to localStorage whenever basket changes
  useEffect(() => {
    localStorage.setItem(EVIDENCE_BASKET_STORAGE_KEY, JSON.stringify(basket));
  }, [basket]);

  const addEvidence = (item: Omit<EvidenceBasketItem, 'added_at'>) => {
    const newItem: EvidenceBasketItem = {
      ...item,
      added_at: new Date().toISOString(),
    };
    setBasket((prev) => {
      // Avoid duplicates
      if (prev.some((i) => i.artifact_id === newItem.artifact_id)) {
        return prev;
      }
      return [...prev, newItem];
    });
  };

  const removeEvidence = (artifactId: string) => {
    setBasket((prev) => prev.filter((item) => item.artifact_id !== artifactId));
  };

  const clearBasket = () => {
    setBasket([]);
  };

  const exportToCSV = () => {
    if (basket.length === 0) return;
    
    const headers = ['Filename', 'Type', 'Page', 'Confidence', 'Excerpt', 'Added At'];
    const rows = basket.map(item => [
      item.artifact_filename,
      item.artifact_type,
      item.page_number?.toString() || '',
      (item.confidence * 100).toFixed(1) + '%',
      item.excerpt.replace(/"/g, '""'),
      new Date(item.added_at).toLocaleString(),
    ]);
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evidence-basket-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToJSON = () => {
    if (basket.length === 0) return;
    
    const blob = new Blob([JSON.stringify(basket, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evidence-basket-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card title="Evidence Basket" className="h-full">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Badge variant="default">{basket.length} items</Badge>
          <div className="flex gap-1">
            {basket.length > 0 && (
              <>
                <Button variant="ghost" size="sm" onClick={exportToCSV} title="Export to CSV">
                  <Download className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={exportToJSON} title="Export to JSON">
                  <Download className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={clearBasket}>
                  Clear All
                </Button>
              </>
            )}
          </div>
        </div>

        {basket.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="mx-auto h-12 w-12 mb-2 opacity-50" />
            <p>No evidence selected</p>
            <p className="text-sm mt-1">Add evidence from search results</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {basket.map((item) => (
              <div
                key={item.artifact_id}
                className="border rounded-lg p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{item.artifact_filename}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {item.artifact_type} {item.page_number && `• Page ${item.page_number}`}
                    </p>
                    {item.excerpt && (
                      <p className="text-xs text-gray-600 mt-2 line-clamp-2">{item.excerpt}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="default" className="text-xs">
                        {Math.round(item.confidence * 100)}% confidence
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeEvidence(item.artifact_id)}
                    className="ml-2"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// Export hook for adding evidence from other components
export function useEvidenceBasket() {
  const [basket, setBasket] = useState<EvidenceBasketItem[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem(EVIDENCE_BASKET_STORAGE_KEY);
    if (stored) {
      try {
        setBasket(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load evidence basket:', e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(EVIDENCE_BASKET_STORAGE_KEY, JSON.stringify(basket));
  }, [basket]);

  // Sync with Evidence API - load evidence details for items in basket
  useEffect(() => {
    const loadEvidenceDetails = async () => {
      const { getEvidence } = await import('@tracekit/core');
      const evidenceIds = basket
        .filter(item => item.evidence_id)
        .map(item => item.evidence_id!);
      
      if (evidenceIds.length > 0) {
        try {
          const evidenceDetails = await Promise.all(
            evidenceIds.map(id => getEvidence(id).catch(() => null))
          );
          // Update basket items with evidence details if needed
          // This ensures we have the latest evidence data
        } catch (error) {
          console.error('Failed to load evidence details:', error);
        }
      }
    };
    
    if (basket.length > 0) {
      loadEvidenceDetails();
    }
  }, [basket]);

  const addEvidence = (item: Omit<EvidenceBasketItem, 'added_at'>) => {
    setBasket((prev) => {
      if (prev.some((i) => i.artifact_id === item.artifact_id)) {
        return prev;
      }
      return [...prev, { ...item, added_at: new Date().toISOString() }];
    });
  };

  const removeEvidence = (artifactId: string) => {
    setBasket((prev) => prev.filter((item) => item.artifact_id !== artifactId));
  };

  const clearBasket = () => {
    setBasket([]);
  };

  const getEvidenceIds = () => {
    return basket.map((item) => item.artifact_id);
  };

  const exportToCSV = () => {
    if (basket.length === 0) return;
    
    const headers = ['Filename', 'Type', 'Page', 'Confidence', 'Excerpt', 'Added At'];
    const rows = basket.map(item => [
      item.artifact_filename,
      item.artifact_type,
      item.page_number?.toString() || '',
      (item.confidence * 100).toFixed(1) + '%',
      item.excerpt.replace(/"/g, '""'),
      new Date(item.added_at).toLocaleString(),
    ]);
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evidence-basket-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToJSON = () => {
    if (basket.length === 0) return;
    
    const blob = new Blob([JSON.stringify(basket, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evidence-basket-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return {
    basket,
    addEvidence,
    removeEvidence,
    clearBasket,
    getEvidenceIds,
    exportToCSV,
    exportToJSON,
  };
}

